import React, { useEffect, useRef, useState } from "react";
import { Card, CardBody, Col, Form, Button, Row } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { LocationService } from "./service";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import DeleteModal from "../../Common/DeleteModal";
import BasicModal from "../../Common/BasicModal";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import Accordion from "react-bootstrap/Accordion";

function mapWithIsExpanded(nodes: any[], search: string): any[] {
  const searchTerm = search.trim().toLowerCase();
  if (!searchTerm) {
    // If search is empty, nothing should be expanded
    return nodes.map(node => ({
      ...node,
      isExpanded: false,
      children: node.children ? mapWithIsExpanded(node.children, search) : undefined
    }));
  }

  // Helper to check if any descendant matches
  function hasDescendantMatch(node: any): boolean {
    if (Array.isArray(node.children) && node.children.length > 0) {
      return node.children.some((child: any) =>
        (child.name && child.name.toLowerCase().includes(searchTerm)) ||
        hasDescendantMatch(child)
      );
    }
    return false;
  }

  return nodes.map(node => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const shouldExpand = hasChildren && hasDescendantMatch(node);

    return {
      ...node,
      isExpanded: shouldExpand,
      children: hasChildren ? mapWithIsExpanded(node.children, search) : undefined
    };
  });
}

// Helper to collect all eventKeys for nodes with isExpanded === true
const collectExpandedKeys = (locations: any[], prefix = ""): string[] => {
  let keys: string[] = [];
  locations.forEach((loc, idx) => {
    const key = prefix ? `${prefix}-${idx}` : `${idx}`;
    if (loc.isExpanded) {
      keys.push(key);
    }
    if (Array.isArray(loc.children) && loc.children.length > 0) {
      keys = keys.concat(collectExpandedKeys(loc.children, key));
    }
  });
  return keys;
};

const LocationList = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [locations, setLocations] = useState<any[]>([]);
  const [termSearch, setTermSearch] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<any>(null);
  const [name, setName] = useState("");
  const [parent, setParent] = useState<number | null>(null);
  const [parentLocation, setParentLocation] = useState<any>(null);
  const [locationToDelete, setLocationToDelete] = useState<number | null>(null);
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedTerm(termSearch), 400);
    return () => clearTimeout(handler);
  }, [termSearch]);

  // Fetch locations on debounced search
  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line
  }, [debouncedTerm]);

  const fetchLocations = async () => {
    try {
      const response = await LocationService.getList({ term: debouncedTerm });
      const mapped = mapWithIsExpanded(response, debouncedTerm);
      setLocations(mapped);

      // Auto-expand parents if any node isExpanded
      const autoExpandKeys = collectExpandedKeys(mapped);
      setActiveKeys(autoExpandKeys);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const handleOpenModal = (location: any = null, parentLocationObj: any = null) => {
    setCurrentLocation(location);
    setName(location ? location.name : "");
    setParent(parentLocationObj ? parentLocationObj.id : null);
    setParentLocation(parentLocationObj);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentLocation(null);
    setName("");
    setParent(null);
    setParentLocation(null);
  };

  const handleSaveLocation = async () => {
    try {
      const data = { name, parent };
      if (currentLocation) {
        await LocationService.update(data, currentLocation.id);
        SuccessToast(t("location.successUpdate"));
      } else {
        await LocationService.create(data);
        SuccessToast(t("location.successCreate"));
      }
      fetchLocations();
      handleCloseModal();
    } catch (error) {
      ErrorToast(error);
    }
  };

  const handleOpenDeleteModal = (locationId: number) => {
    setLocationToDelete(locationId);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setLocationToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (locationToDelete !== null) {
      try {
        await LocationService.delete(locationToDelete);
        SuccessToast(t("location.successDelete"));
        setShowDeleteModal(false);
        setLocationToDelete(null);
        fetchLocations();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  // Example color logic (customize as needed)
  const getBgColor = (level: number) => {
    if (level === 1) return theme === "dark" ? "#222" : "#e9f5ff";
    if (level === 2) return theme === "dark" ? "#333" : "#f0f7ff";
    return theme === "dark" ? "#222" : "#f8f9fa";
  };

  // Helper to generate unique keys for nested accordions
  const getEventKey = (prefix: string, idx: number) => (prefix ? `${prefix}-${idx}` : `${idx}`);

  // Handler for manual expand/collapse
  const handleAccordionToggle = (eventKey: string) => {
    setActiveKeys(prev =>
      prev.includes(eventKey) ? prev.filter(key => key !== eventKey) : [...prev, eventKey]
    );
  };

  const renderLocations = (locations: any[], level: number = 0, prefix: string = "") => {
    return (
      <Accordion alwaysOpen activeKey={activeKeys}>
        {locations.map((location, idx) => {
          const eventKey = getEventKey(prefix, idx);

          // Level 3: Not collapsible, colored background
          if (location.level === 3) {
            return (
              <div
                key={location.id}
                className="d-flex justify-content-between align-items-center py-2 px-3 mb-1"
                style={{
                  background: getBgColor(3),
                  borderRadius: "6px",
                }}
              >
                <span style={{ color: theme === "dark" ? "#fff" : "#000" }}>
                  {location.name}
                </span>
                <div className="d-flex">
                  <li
                    className="list-inline-item"
                    role="button"
                    onClick={() => handleOpenModal(location)}
                  >
                    <span className="avtar avtar-s btn-link-success btn-pc-default">
                      <i
                        className="ti ti-edit f-20"
                        style={{ color: theme === "dark" ? "#fff" : "#000" }}
                      ></i>
                    </span>
                  </li>
                  <li className="list-inline-item">
                    <span
                      role="button"
                      className="avtar avtar-s btn-link-danger btn-pc-default"
                      onClick={() => handleOpenDeleteModal(location.id)}
                    >
                      <i
                        className="ti ti-trash f-20"
                        style={{ color: theme === "dark" ? "#fff" : "#000" }}
                      ></i>
                    </span>
                  </li>
                </div>
              </div>
            );
          }

          // If no children, render as a styled div (no collapse icon)
          if (!location.children || location.children.length === 0) {
            return (
              <div
                key={location.id}
                className="d-flex justify-content-between align-items-center py-2 px-3 mb-1"
                style={{
                  background: getBgColor(location.level),
                  borderRadius: "6px",
                }}
              >
                <span style={{ color: theme === "dark" ? "#fff" : "#000" }}>
                  {location.name}
                </span>
                <div className="d-flex">
                  {location.level !== 3 && (
                    <li
                      className="list-inline-item"
                      onClick={() => handleOpenModal(null, location)}
                    >
                      <span
                        role="button"
                        className="avtar avtar-s btn-link-success btn-pc-default"
                      >
                        <i
                          className="ti ti-plus f-20"
                          style={{ color: theme === "dark" ? "#fff" : "#000" }}
                        ></i>
                      </span>
                    </li>
                  )}
                  <li
                    className="list-inline-item"
                    role="button"
                    onClick={() => handleOpenModal(location)}
                  >
                    <span className="avtar avtar-s btn-link-success btn-pc-default">
                      <i
                        className="ti ti-edit f-20"
                        style={{ color: theme === "dark" ? "#fff" : "#000" }}
                      ></i>
                    </span>
                  </li>
                  <li className="list-inline-item">
                    <span
                      role="button"
                      className="avtar avtar-s btn-link-danger btn-pc-default"
                      onClick={() => handleOpenDeleteModal(location.id)}
                    >
                      <i
                        className="ti ti-trash f-20"
                        style={{ color: theme === "dark" ? "#fff" : "#000" }}
                      ></i>
                    </span>
                  </li>
                </div>
              </div>
            );
          }

          // Otherwise, render as Accordion.Item (collapsible)
          return (
            <Accordion.Item eventKey={eventKey} key={location.id}>
              <Accordion.Header
                style={{
                  background: getBgColor(location.level),
                  borderRadius: "6px",
                  color: theme === "dark" ? "#fff" : "#000",
                }}
                onClick={e => {
                  e.stopPropagation();
                  handleAccordionToggle(eventKey);
                }}
              >
                <div className="d-flex justify-content-between align-items-center w-100">
                  <span style={{ color: theme === "dark" ? "#fff" : "#000" }}>
                    {location.name}
                  </span>
                  <div className="d-flex">
                    {location.level !== 3 && (
                      <li
                        className="list-inline-item"
                        onClick={e => {
                          e.stopPropagation();
                          handleOpenModal(null, location);
                        }}
                      >
                        <span
                          role="button"
                          className="avtar avtar-s btn-link-success btn-pc-default"
                        >
                          <i
                            className="ti ti-plus f-20"
                            style={{ color: theme === "dark" ? "#fff" : "#000" }}
                          ></i>
                        </span>
                      </li>
                    )}
                    <li
                      className="list-inline-item"
                      role="button"
                      onClick={e => {
                        e.stopPropagation();
                        handleOpenModal(location);
                      }}
                    >
                      <span className="avtar avtar-s btn-link-success btn-pc-default">
                        <i
                          className="ti ti-edit f-20"
                          style={{ color: theme === "dark" ? "#fff" : "#000" }}
                        ></i>
                      </span>
                    </li>
                    <li className="list-inline-item">
                      <span
                        role="button"
                        className="avtar avtar-s btn-link-danger btn-pc-default"
                        onClick={e => {
                          e.stopPropagation();
                          handleOpenDeleteModal(location.id);
                        }}
                      >
                        <i
                          className="ti ti-trash f-20"
                          style={{ color: theme === "dark" ? "#fff" : "#000" }}
                        ></i>
                      </span>
                    </li>
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                {location.children && renderLocations(location.children, level + 1, eventKey)}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    );
  };

  return (
    <React.Fragment>
      <Row>
        <Col sm={12}>
          <Card bg={theme === "dark" ? "dark" : "light"}>
            <CardBody>
              <div className="text-end mb-3">
                <Button variant="primary" onClick={() => handleOpenModal()}>
                  {t("location.addLocation")}
                </Button>
              </div>
              <div className="mb-3 d-flex justify-content-end">
                <input
                  type="search"
                  className="form-control w-auto"
                  placeholder={t("location.searchPlaceholder") || "Search by name"}
                  value={termSearch}
                  onChange={e => setTermSearch(e.target.value)}
                  style={{ minWidth: 250 }}
                />
              </div>
              <div>{renderLocations(locations)}</div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Modal */}
      <BasicModal
        show={showModal}
        handleClose={handleCloseModal}
        handleSave={handleSaveLocation}
        title={currentLocation ? t("location.edit") : t("location.add")}
      >
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t("location.name")} <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("location.enterName")}
              required
            />
          </Form.Group>
          {parentLocation && (
            <Form.Group className="mb-3">
              <Form.Label>{t("location.parentLocation")}</Form.Label>
              <Form.Control
                type="text"
                value={`${parentLocation.name} (ID: ${parentLocation.id})`}
                disabled
              />
            </Form.Group>
          )}
        </Form>
      </BasicModal>

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={handleCloseDeleteModal}
        handleDeleteId={handleDeleteConfirm}
        name={t("location.name")}
      />
    </React.Fragment>
  );
};

export default LocationList;